import { Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Boxes } from 'lucide-react';
import { formatMoney, formatDate } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceRow,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** One Hosting table row: a base configoption (storage/website/mysql) or addon. */
interface HostingRow {
  key: string;
  serviceName?: string | null;
  resourceName: string;
  unit?: string;
  commitment_value?: number;
  commitment_prepaid?: boolean | null;
  commitment_price_per_unit?: number;
  commitment_price_total?: number;
  isAddon?: boolean;
  employee_names?: string[];
  service_start_date?: string | null;
}

const muted = { color: 'var(--vn-fg-muted)' } as const;
const nf = new Intl.NumberFormat('en-US');
const rightHeader = (label: string) => () => <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>;

const RESOURCE_LABELS: Record<string, string> = {
  storage: 'Storage',
  website: 'Websites',
  mysql: 'MySQL DBs',
};

function resourceLabel(name: string): string {
  return RESOURCE_LABELS[name] ?? name;
}

function quantityCell(value?: number, unit?: string): React.ReactNode {
  if (!value) return <span style={{ ...muted, fontSize: 'var(--vn-font-size-xs)' }}>—</span>;
  const unitLabel = unit && unit !== 'qty' ? ` ${unit.toUpperCase()}` : '';
  return (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {nf.format(value)}{unitLabel}
    </span>
  );
}

function buildRows(
  detail: ServiceDetail,
  base: ServiceConfigOption[],
  addons: (ServiceRow & { configoptions?: ServiceConfigOption[] })[],
): HostingRow[] {
  const rows: HostingRow[] = base.map((c) => ({
    key: `base-${c.service_configoption_id}`,
    serviceName: detail.service_name,
    resourceName: resourceLabel(c.name),
    unit: c.unit,
    commitment_value: c.commitment_value,
    commitment_prepaid: c.commitment_prepaid,
    commitment_price_per_unit: c.commitment_price_per_unit,
    commitment_price_total: c.commitment_price_total,
    employee_names: detail.employee_names,
    service_start_date: detail.service_start_date,
  }));
  for (const a of addons) {
    rows.push({
      key: `addon-${a.service_id}`,
      serviceName: a.service_name,
      resourceName: `[ADDON] - ${a.service_name}`,
      isAddon: true,
      commitment_value: a.configoptions?.[0]?.commitment_value ?? 0,
      commitment_price_total: a.service_price_register,
      employee_names: detail.employee_names,
      service_start_date: a.service_start_date,
    });
  }
  return rows;
}

/**
 * Cloud.Hosting Information config table — one row per resource configoption
 * (storage/website/mysql) plus addon services. Mirrors legacy Cloud Hosting
 * Information view.
 */
export function HostingConfigTable({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {v ? formatMoney(v, currency) : '—'}
    </span>
  );

  const baseRows = base.data?.results ?? [];
  const rows = buildRows(detail, baseRows, addons.data?.results ?? []);
  const hasPrice = baseRows.some((c) => (c.commitment_price_per_unit ?? 0) > 0);

  const columns: ColumnDef<HostingRow, unknown>[] = [
    {
      id: 'service',
      header: 'Service',
      cell: ({ row }) => row.original.serviceName || <span style={muted}>—</span>,
    },
    {
      id: 'resource',
      header: 'Resource',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.resourceName}
        </span>
      ),
    },
    {
      id: 'commitment_value',
      header: rightHeader('Quantity'),
      cell: ({ row }) => quantityCell(row.original.commitment_value, row.original.unit),
    },
  ];

  if (hasPrice) {
    columns.push({
      id: 'commitment_prepaid',
      header: 'Payment',
      cell: ({ row }) =>
        (row.original.commitment_prepaid !== null && row.original.commitment_prepaid !== undefined) ? (
          <Chip
            appearance="subtle"
            tone={row.original.commitment_prepaid ? 'success' : 'neutral'}
            size="sm"
          >
            {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
          </Chip>
        ) : null,
    });
    columns.push({
      id: 'unit_price',
      header: rightHeader('Unit price'),
      cell: ({ row }) => money(row.original.commitment_price_per_unit),
    });
    columns.push({
      id: 'total_price',
      header: rightHeader('Total price'),
      cell: ({ row }) => money(row.original.commitment_price_total),
    });
  }

  columns.push({
    id: 'sales',
    header: 'Sales',
    cell: ({ row }) =>
      row.original.employee_names?.filter(Boolean).join(', ') || <span style={muted}>—</span>,
  });

  columns.push({
    id: 'start',
    header: rightHeader('Start date'),
    cell: ({ row }) => (
      <span style={{ display: 'block', textAlign: 'right' }}>
        {formatDate(row.original.service_start_date)}
      </span>
    ),
  });

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.key}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title="No configuration"
              description="No resource configuration found for this hosting plan."
            />
          }
        />
      </CardBody>
    </Card>
  );
}
